/**
 * KnowledgeView — immersive vanilla-TS Knowledge experience for V1.
 *
 * Layout per Alex (2026-06-12): ~80% Medium oben, unten das
 * BESTEHENDE Wanderlaut-Panel-Design (Kategorie · Titel · Info-Text)
 * plus schlanke Annotation-Nummern-Chips. Alle visuellen Tokens kommen
 * als Style-Guide-Erweiterung aus KnowledgeView.css (kv-exp-*) — keine
 * erfundenen Farben/Fonts in der View.
 *
 * Rendering is delegated to the shared renderer-registry from
 * `@arkturian/audio-guide/knowledge`: 2D pin overlay with pinch-zoom,
 * HLS video, lazy 3D with raycastable pin-sprites.
 *
 * Annotations are doubly accessible — pins ON the medium and as an
 * audio-card list (play per entry; entries without audio.url get
 * on-demand TTS synthesis via cloud-api).
 *
 * Mount/unmount lifecycle:
 *   - mount(container) attaches a position:fixed fullscreen root.
 *   - destroy() removes nodes, releases audio (+ blob URLs), and
 *     destroy()s the active renderer-handle (disposes the GL context
 *     — CRITICAL, three.js leaks aggressively without it).
 */
import './KnowledgeView.css';
import {
  buildDefaultRegistry,
  buildMediaSequence,
  canHandle3D,
  DEFAULT_KNOWLEDGE_THEME,
  type AnnotatedKnowledge,
  type AnnotationItem as LibAnnotationItem,
  type KnowledgeRendererRegistry,
  type MediaSequenceItem,
  type RendererHandle,
} from '@arkturian/audio-guide/knowledge';

export interface AnnotationItem {
  id: string;
  label: string;
  description: string;
  anchor?: { x: number; y: number };
  position3D?: { x: number; y: number; z: number } | null;
  audio?: { url: string } | null;
}

export interface KnowledgeMedia {
  storage_id: number;
  kind: 'illustration' | 'field_photo' | 'field_video' | 'video' | '3d_model';
  url: string;
  thumbnail_url?: string;
  display?: boolean;
}

export interface KnowledgeData {
  /** Top-level source-image id — the photo the annotation anchors
   *  were curated against (NOT in media[]). */
  storage_id?: number;
  source_thumbnail_url?: string;
  detail_level?: string;
  binom?: string;
  common_name_de?: string;
  object_title?: string;
  object_description?: string;
  short?: string;
  wiki?: string;
  facts?: Array<{ label: string; value: string }>;
  annotations?: AnnotationItem[];
  media?: KnowledgeMedia[];
}

export interface KnowledgeViewOptions {
  /** cloud-api base for on-demand TTS synthesis when an annotation has
   *  no pre-rendered audio.url (POST {base}/api/tts-speak). Without it
   *  the play button only works for annotations that ship a URL. */
  ttsApiBase?: string;
  /** Called when the user taps the back button. The host owns the
   *  actual teardown (destroy() + its own modal/host bookkeeping). */
  onClose?: () => void;
}

const GOLD = '#c4a96a';

/** Shared registry instance — renderers are stateless until mount(),
 *  and the pin-texture memo-cache profits from re-use across opens. */
const registry: KnowledgeRendererRegistry = buildDefaultRegistry();

export class KnowledgeView {
  private _root: HTMLElement | null = null;
  private _data: KnowledgeData;
  private _opts: KnowledgeViewOptions;
  private _audio: HTMLAudioElement | null = null;
  /** Monotonic token — invalidates in-flight TTS fetches when a newer
   *  play request (or destroy) supersedes them. */
  private _playToken = 0;
  private _handle: RendererHandle | null = null;
  private _mediaHost: HTMLElement | null = null;
  private _sequence: MediaSequenceItem[] = [];
  private _currentIdx = 0;
  private _activeId: string | null = null;

  constructor(data: KnowledgeData, opts: KnowledgeViewOptions = {}) {
    this._data = data;
    this._opts = opts;
  }

  /** Build the fullscreen DOM. Call destroy() to unmount. */
  mount(container: HTMLElement): void {
    const root = document.createElement('div');
    root.className = 'kv-exp-root';
    container.appendChild(root);
    this._root = root;

    this._sequence = buildMediaSequence({
      knowledge: this._toAnnotated(),
      is3DCapable: canHandle3D(),
    });

    // ~80% Medium oben (flex:1), das Panel unten nimmt nur seine
    // natürliche Höhe — Alex' Layout-Vorgabe 2026-06-12.
    const mediaHost = document.createElement('div');
    mediaHost.className = 'kv-exp-media';
    root.appendChild(mediaHost);
    this._mediaHost = mediaHost;

    this._renderPanel();
    this._setupTapToClose();

    // 3D als Default, wenn ein 3D-Modell vorhanden ist — sonst das erste
    // 2D-Medium. (Fällt bei 3D-Ladefehler in _mountItem auf 2D zurück.)
    if (this._sequence.length > 0) {
      const glb = this._sequence.findIndex((it) => it.kind === '3d_model');
      this._mountItem(glb >= 0 ? glb : 0);
    }
  }

  /** Kein Zurück-Button (UX-Bruch in einer Wissens-Vollbildansicht):
   *  ein sauberer Tap auf den Medien-Hintergrund schließt. Pinch/Pan
   *  (Bewegung) und Taps auf Pin/Toggle/Dots/Panel bleiben unberührt. */
  private _setupTapToClose(): void {
    if (!this._root) return;
    let down: { x: number; y: number } | null = null;
    this._root.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY }; });
    this._root.addEventListener('pointerup', (e) => {
      const d = down;
      down = null;
      if (!d) return;
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) return; // war Drag/Pinch
      const t = e.target as HTMLElement;
      if (t.closest('button') || t.closest('.kv-exp-panel')) return; // Pin/Toggle/Dot/Panel
      this._opts.onClose?.();
    });
  }

  destroy(): void {
    this._playToken++;
    if (this._handle) {
      this._handle.destroy();
      this._handle = null;
    }
    this._stopAudio();
    this._root?.remove();
    this._root = null;
    this._mediaHost = null;
    this._sequence = [];
  }

  // ── data adapter ──

  private _title(): string {
    return this._data.common_name_de || this._data.object_title || this._data.binom || '';
  }

  /** Adapt the V1 wire-shape to the library's AnnotatedKnowledge. */
  private _toAnnotated(): AnnotatedKnowledge {
    return {
      id: 0,
      storage_id: this._data.storage_id ?? 0,
      detail_level: this._data.detail_level ?? '',
      source_thumbnail_url: this._data.source_thumbnail_url ?? null,
      object_title: this._title(),
      object_description: this._data.short || '',
      binom: this._data.binom ?? null,
      common_name_de: this._data.common_name_de ?? null,
      short: this._data.short ?? null,
      wiki: this._data.wiki ?? null,
      media: (this._data.media || []).map((m) => ({
        kind: m.kind,
        storage_id: m.storage_id,
        url: m.url,
        thumbnail_url: m.thumbnail_url,
        display: m.display,
      })),
      annotations: (this._data.annotations || []) as LibAnnotationItem[],
    };
  }

  // ── renderer host ──

  /** Mount the sequence item at `idx` through the registry. One live
   *  renderer at a time keeps the GL-context story sane. */
  private _mountItem(idx: number): void {
    if (!this._mediaHost) return;
    const item = this._sequence[idx];
    if (!item) return;
    const renderer = registry.resolve(item);
    if (!renderer) return;

    this._handle?.destroy();
    this._handle = null;
    this._mediaHost.innerHTML = '';
    this._currentIdx = idx;
    this._syncModeToggle();
    this._syncDots();

    this._handle = renderer.mount(this._mediaHost, item, {
      activeAnnotationId: this._activeId,
      hoveredAnnotationId: null,
      onSelectAnnotation: (id): void => this._selectAnnotation(id, true),
      onHoverAnnotation: (): void => undefined,
      onPlayAnnotationAudio: (_id, url): void => {
        if (url) this._playUrl(url);
      },
      theme: DEFAULT_KNOWLEDGE_THEME,
      onError: (err): void => {
        console.warn('KnowledgeView renderer error', err);
        // 3D failed at runtime — fall back to the first 2D item so the
        // user still sees something instead of a blank canvas.
        if (item.kind === '3d_model') {
          const fallback = this._sequence.findIndex((it) => it.kind !== '3d_model');
          if (fallback >= 0) this._mountItem(fallback);
        }
      },
    });
  }

  // ── overlay chrome ──

  private _renderPanel(): void {
    if (!this._root || !this._mediaHost) return;
    const anns = this._data.annotations || [];

    // Media-Dots schweben am unteren Rand des Medien-Bereichs.
    if (this._sequence.length > 1) {
      const dots = document.createElement('div');
      dots.className = 'kv-exp-dots';
      this._sequence.forEach((_it, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'kv-exp-dot';
        d.setAttribute('aria-label', `Zu Medium ${i + 1}`);
        d.dataset.idx = String(i);
        d.addEventListener('click', () => {
          this._selectAnnotation(null, false);
          this._mountItem(i);
        });
        dots.appendChild(d);
      });
      this._mediaHost.appendChild(dots);
    }

    // Bestehendes Wanderlaut-Panel-Design: Kategorie-Zeile (gold),
    // Titel, Info-Text — plus schlanke Annotation-Chips.
    const panel = document.createElement('div');
    panel.className = 'kv-exp-panel';
    this._root.appendChild(panel);

    const metaRow = document.createElement('div');
    metaRow.className = 'kv-exp-meta-row';
    panel.appendChild(metaRow);

    const cat = document.createElement('div');
    cat.className = 'kv-exp-cat';
    cat.textContent = ['Wissen', this._data.detail_level, anns.length > 0 ? `${anns.length} Merkmale` : null]
      .filter(Boolean).join(' · ');
    metaRow.appendChild(cat);

    const glbIdx = this._sequence.findIndex((it) => it.kind === '3d_model');
    if (glbIdx >= 0) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'kv-exp-toggle';
      toggle.addEventListener('click', () => {
        const is3D = this._sequence[this._currentIdx]?.kind === '3d_model';
        this._mountItem(is3D ? 0 : glbIdx);
      });
      metaRow.appendChild(toggle);
    }

    const title = document.createElement('div');
    title.className = 'kv-exp-title';
    title.textContent = this._title();
    panel.appendChild(title);
    if (this._data.binom && this._data.binom !== this._title()) {
      const binom = document.createElement('div');
      binom.className = 'kv-exp-binom';
      binom.textContent = this._data.binom;
      panel.appendChild(binom);
    }

    if (anns.length > 0) {
      const chips = document.createElement('div');
      chips.className = 'kv-exp-chips';
      anns.forEach((ann, i) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'kv-exp-chip';
        chip.dataset.annId = ann.id;
        chip.textContent = String(i + 1);
        chip.title = ann.label;
        chip.setAttribute('aria-label', `${i + 1}. ${ann.label}`);
        chip.addEventListener('click', () => {
          const next = this._activeId === ann.id ? null : ann.id;
          this._selectAnnotation(next, false);
          if (next) void this._playAnnotationAudio(ann);
          else this._stopAudio();
        });
        chips.appendChild(chip);
      });
      panel.appendChild(chips);
    }

    const text = document.createElement('div');
    text.className = 'kv-exp-text';
    panel.appendChild(text);
    this._syncText();
  }

  /** Panel-Text: aktive Annotation (Label fett + Beschreibung) oder
   *  als Default die Objekt-Beschreibung des Items. */
  private _syncText(): void {
    const text = this._root?.querySelector<HTMLElement>('.kv-exp-text');
    if (!text) return;
    const ann = (this._data.annotations || []).find((a) => a.id === this._activeId);
    text.innerHTML = '';
    if (ann) {
      const b = document.createElement('b');
      b.textContent = `${ann.label}. `;
      text.appendChild(b);
      text.appendChild(document.createTextNode(ann.description));
    } else {
      text.textContent = this._data.short || this._data.object_description || '';
    }
  }

  // ── selection sync (pins ↔ cards) ──

  private _selectAnnotation(id: string | null, fromRenderer: boolean): void {
    this._activeId = id;
    this._handle?.update({ activeAnnotationId: id });
    this._root?.querySelectorAll<HTMLElement>('.kv-exp-chip').forEach((chip) => {
      chip.classList.toggle('active', chip.dataset.annId === id && id !== null);
    });
    this._syncText();
    if (fromRenderer && id) {
      const ann = (this._data.annotations || []).find((a) => a.id === id);
      if (ann) void this._playAnnotationAudio(ann);
    }
  }

  private _syncModeToggle(): void {
    const toggle = this._root?.querySelector<HTMLElement>('.kv-exp-toggle');
    if (toggle) {
      const is3D = this._sequence[this._currentIdx]?.kind === '3d_model';
      toggle.textContent = is3D ? '2D' : '3D';
      toggle.setAttribute('aria-label', is3D ? 'Zur 2D-Ansicht' : 'Zur 3D-Ansicht');
      toggle.classList.toggle('active3d', is3D);
    }
    this._syncDots();
  }

  private _syncDots(): void {
    this._root?.querySelectorAll<HTMLElement>('.kv-exp-dot').forEach((d) => {
      d.classList.toggle('active', Number(d.dataset.idx) === this._currentIdx);
    });
  }

  // ── audio (pre-rendered URL or on-demand TTS) ──

  private async _playAnnotationAudio(a: AnnotationItem): Promise<void> {
    if (a.audio?.url) {
      this._playUrl(a.audio.url);
      return;
    }
    const base = this._opts.ttsApiBase;
    if (!base || !a.description) return;
    const token = ++this._playToken;
    try {
      const r = await fetch(`${base.replace(/\/+$/, '')}/api/tts-speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: a.description }),
      });
      if (!r.ok) throw new Error(`tts HTTP ${r.status}`);
      const buf = await r.arrayBuffer();
      if (token !== this._playToken) return; // superseded / destroyed
      const url = URL.createObjectURL(new Blob([buf], { type: 'audio/mpeg' }));
      this._playUrl(url);
      this._audio?.addEventListener('ended', () => URL.revokeObjectURL(url), { once: true });
    } catch (err) {
      console.warn('KnowledgeView annotation TTS failed', err);
    }
  }

  private _playUrl(url: string): void {
    this._stopAudio();
    const audio = new Audio(url);
    audio.play().catch(err => console.warn('KnowledgeView audio play failed', err));
    this._audio = audio;
  }

  private _stopAudio(): void {
    if (!this._audio) return;
    this._audio.pause();
    // Superseded BEFORE 'ended' fired → its revoke-listener never runs.
    if (this._audio.src.startsWith('blob:')) URL.revokeObjectURL(this._audio.src);
    this._audio.src = '';
    this._audio = null;
  }
}
